import { useState, useRef, useCallback, useEffect } from 'react';
import { useTabVisibility } from '../ui/useTabVisibility';
import { debugError, debugLog, debugWarn } from '../../utils/debugLog';

/**
 * Configuration options for smart polling behavior
 */
export interface SmartPollingConfig {
  /** Initial delay before starting polling (default: 5 minutes) */
  initialDelay?: number;
  /** Base interval for polling when active (default: 30 seconds) */
  activeInterval?: number;
  /** Base interval for polling when inactive (default: 5 minutes) */
  inactiveInterval?: number;
  /** Maximum number of retry attempts before circuit breaker opens (default: 10) */
  maxRetries?: number;
  /** Maximum interval between retries (default: 10 minutes) */
  maxInterval?: number;
  /** Multiplier for exponential backoff (default: 1.5) */
  backoffMultiplier?: number;
  /** Whether to start polling immediately (default: false) */
  startImmediately?: boolean;
  /** Whether to immediately check when tab becomes visible (default: true) */
  immediateOnVisible?: boolean;
  /** Circuit breaker configuration */
  circuitBreaker?: {
    /** Time window for failure rate calculation in ms (default: 5 minutes) */
    failureWindow?: number;
    /** Failure rate threshold to open circuit (0-1, default: 0.5) */
    failureThreshold?: number;
    /** Time to wait before attempting recovery in ms (default: 30 seconds) */
    recoveryTimeout?: number;
    /** Maximum number of recovery attempts (default: 3) */
    maxRecoveryAttempts?: number;
  };
}

/**
 * State information about the polling operation
 */
export interface PollingState {
  /** Whether polling is currently active */
  isPolling: boolean;
  /** Whether the user/tab is currently active */
  isActive: boolean;
  /** Current retry attempt count */
  retryCount: number;
  /** Whether the circuit breaker is open (polling stopped due to too many failures) */
  isCircuitOpen: boolean;
  /** Current polling interval in milliseconds */
  currentInterval: number;
  /** Timestamp of last successful poll */
  lastSuccessTime: number | null;
  /** Timestamp of last poll attempt */
  lastAttemptTime: number | null;
  /** Circuit breaker state information */
  circuitBreakerState: {
    /** Current failure rate (0-1) */
    failureRate: number;
    /** Number of failures in current window */
    failuresInWindow: number;
    /** Total attempts in current window */
    attemptsInWindow: number;
    /** Time when circuit breaker opened */
    openedAt: number | null;
    /** Number of recovery attempts made */
    recoveryAttempts: number;
    /** Whether currently in recovery mode */
    isRecovering: boolean;
  };
}

/**
 * Return type for the useSmartPolling hook
 */
export interface UseSmartPollingReturn {
  /** Current polling state */
  state: PollingState;
  /** Start polling with optional immediate execution */
  startPolling: (executeImmediately?: boolean) => void;
  /** Stop polling and reset state */
  stopPolling: () => void;
  /** Reset circuit breaker and retry count */
  resetCircuit: () => void;
  /** Manually trigger a poll attempt */
  triggerPoll: () => Promise<boolean>;
  /** Force circuit breaker recovery attempt */
  attemptRecovery: () => Promise<boolean>;
}

/**
 * Smart polling hook with exponential backoff, circuit breaker, and activity-based intervals
 * 
 * Features:
 * - Exponential backoff on failures
 * - Circuit breaker pattern to prevent excessive retries
 * - Activity-based polling (different intervals for active/inactive tabs)
 * - Immediate refresh when tab becomes visible (prevents up to 5min delay)
 * - Automatic cleanup on unmount
 * - Memory leak prevention
 * 
 * @param callback - Async function to execute on each poll. Should return true on success, false on failure
 * @param config - Configuration options for polling behavior
 * @returns Object with polling state and control functions
 */
export const useSmartPolling = (
  callback: () => Promise<boolean>,
  config: SmartPollingConfig = {}
): UseSmartPollingReturn => {
  const {
    initialDelay = 5 * 60 * 1000, // 5 minutes
    activeInterval = 30 * 1000, // 30 seconds
    inactiveInterval = 5 * 60 * 1000, // 5 minutes
    maxRetries = 10,
    maxInterval = 10 * 60 * 1000, // 10 minutes
    backoffMultiplier = 1.5,
    startImmediately = false,
    immediateOnVisible = true,
    circuitBreaker = {},
  } = config;

  // Circuit breaker configuration with defaults
  const {
    failureWindow = 5 * 60 * 1000, // 5 minutes
    failureThreshold = 0.5, // 50% failure rate
    recoveryTimeout = 30 * 1000, // 30 seconds
    maxRecoveryAttempts = 3,
  } = circuitBreaker;

  // Use the new useTabVisibility hook for better tab state detection
  const isTabVisible = useTabVisibility();

  // Validate and sanitize intervals to prevent negative values
  const safeActiveInterval = Math.max(0, activeInterval);
  const safeInactiveInterval = Math.max(0, inactiveInterval);
  const safeInitialDelay = Math.max(0, initialDelay);
  const safeMaxInterval = Math.max(0, maxInterval);

  // State management
  const [isPolling, setIsPolling] = useState(startImmediately);
  const [retryCount, setRetryCount] = useState(0);
  const [isCircuitOpen, setIsCircuitOpen] = useState(false);
  const [currentInterval, setCurrentInterval] = useState(safeActiveInterval);
  const [lastSuccessTime, setLastSuccessTime] = useState<number | null>(null);
  const [lastAttemptTime, setLastAttemptTime] = useState<number | null>(null);

  // Enhanced circuit breaker state
  const [circuitBreakerState, setCircuitBreakerState] = useState({
    failureRate: 0,
    failuresInWindow: 0,
    attemptsInWindow: 0,
    openedAt: null as number | null,
    recoveryAttempts: 0,
    isRecovering: false,
  });

  // Refs for timer management and cleanup
  const pollingTimer = useRef<NodeJS.Timeout | null>(null);
  const initialDelayTimer = useRef<NodeJS.Timeout | null>(null);
  const recoveryTimer = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);
  const isPollingRef = useRef(isPolling);
  const previousVisibilityRef = useRef(isTabVisible);
  const retryCountRef = useRef(retryCount);
  
  // Track attempts and failures for circuit breaker
  const attemptsHistory = useRef<Array<{ timestamp: number; success: boolean }>>([]);

  // Keep refs in sync with state
  useEffect(() => {
    isPollingRef.current = isPolling;
  }, [isPolling]);

  useEffect(() => {
    retryCountRef.current = retryCount;
  }, [retryCount]);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (pollingTimer.current) {
      clearInterval(pollingTimer.current);
      pollingTimer.current = null;
    }
    if (initialDelayTimer.current) {
      clearTimeout(initialDelayTimer.current);
      initialDelayTimer.current = null;
    }
    if (recoveryTimer.current) {
      clearTimeout(recoveryTimer.current);
      recoveryTimer.current = null;
    }
  }, []);

  // Schedule automatic recovery attempt
  const scheduleRecovery = useCallback(() => {
    if (recoveryTimer.current) {
      clearTimeout(recoveryTimer.current);
    }
    
    recoveryTimer.current = setTimeout(() => {
      setCircuitBreakerState(prev => {
        if (prev.recoveryAttempts < maxRecoveryAttempts) {
          if (process.env.NODE_ENV === 'development') {
            debugLog(`🔄 Circuit breaker recovery attempt ${prev.recoveryAttempts + 1}/${maxRecoveryAttempts}`);
          }
          
          return {
            ...prev,
            isRecovering: true,
            recoveryAttempts: prev.recoveryAttempts + 1,
          };
        }
        return prev;
      });
    }, recoveryTimeout);
  }, [recoveryTimeout, maxRecoveryAttempts]);

  // Update circuit breaker metrics
  const updateCircuitBreakerMetrics = useCallback((success: boolean) => {
    const now = Date.now();
    const windowStart = now - failureWindow;
    
    // Add current attempt to history
    attemptsHistory.current.push({ timestamp: now, success });
    
    // Remove attempts outside the window
    attemptsHistory.current = attemptsHistory.current.filter(
      attempt => attempt.timestamp > windowStart
    );
    
    // Calculate metrics
    const attemptsInWindow = attemptsHistory.current.length;
    const failuresInWindow = attemptsHistory.current.filter(attempt => !attempt.success).length;
    const failureRate = attemptsInWindow > 0 ? failuresInWindow / attemptsInWindow : 0;
    
    // Check if circuit breaker should open
    // Use either the failure rate threshold OR maxRetries (whichever is reached first)
    const shouldOpenCircuit = !isCircuitOpen && (
      (attemptsInWindow >= 3 && failureRate >= failureThreshold) ||
      (retryCountRef.current >= maxRetries)
    );
    
    if (shouldOpenCircuit) {
      // Update all states synchronously
      setIsCircuitOpen(true);
      setIsPolling(false);
      clearAllTimers();
      
      setCircuitBreakerState(prev => ({
        ...prev,
        failureRate,
        failuresInWindow,
        attemptsInWindow,
        openedAt: now,
        isRecovering: false,
      }));
      
      if (process.env.NODE_ENV === 'development') {
        debugWarn(`🚨 Circuit breaker opened: ${failureRate.toFixed(2)} failure rate (${failuresInWindow}/${attemptsInWindow}) or max retries (${retryCountRef.current}/${maxRetries})`);
      }
      
      // Schedule recovery attempt
      scheduleRecovery();
    } else {
      // Just update metrics
      setCircuitBreakerState(prev => ({
        ...prev,
        failureRate,
        failuresInWindow,
        attemptsInWindow,
      }));
    }
  }, [failureWindow, failureThreshold, isCircuitOpen, clearAllTimers, scheduleRecovery, maxRetries]);

  // Attempt recovery from circuit breaker
  const attemptRecovery = useCallback(async (): Promise<boolean> => {
    if (!isCircuitOpen) {
      return true; // Already recovered
    }
    
    setCircuitBreakerState(prev => ({ ...prev, isRecovering: true }));
    
    try {
      const success = await callbackRef.current();
      
      if (success) {
        // Recovery successful
        setIsCircuitOpen(false);
        retryCountRef.current = 0;
        setRetryCount(0);
        setCurrentInterval(isTabVisible ? safeActiveInterval : safeInactiveInterval);
        setLastSuccessTime(Date.now());
        
        setCircuitBreakerState(prev => ({
          ...prev,
          isRecovering: false,
          recoveryAttempts: 0,
          openedAt: null,
        }));
        
        updateCircuitBreakerMetrics(true);
        
        if (process.env.NODE_ENV === 'development') {
          debugLog('✅ Circuit breaker recovery successful');
        }
        
        // Resume polling
        setIsPolling(true);
        return true;
      } else {
        // Recovery failed
        updateCircuitBreakerMetrics(false);
        setCircuitBreakerState(prev => ({ ...prev, isRecovering: false }));
        
        // Schedule next recovery attempt if not exceeded max attempts
        if (circuitBreakerState.recoveryAttempts < maxRecoveryAttempts) {
          scheduleRecovery();
        } else {
          if (process.env.NODE_ENV === 'development') {
            debugWarn('❌ Circuit breaker recovery failed - max attempts exceeded');
          }
        }
        
        return false;
      }
    } catch (error) {
      debugError('Circuit breaker recovery error:', error);
      updateCircuitBreakerMetrics(false);
      setCircuitBreakerState(prev => ({ ...prev, isRecovering: false }));
      
      if (circuitBreakerState.recoveryAttempts < maxRecoveryAttempts) {
        scheduleRecovery();
      }
      
      return false;
    }
  }, [isCircuitOpen, isTabVisible, safeActiveInterval, safeInactiveInterval, circuitBreakerState.recoveryAttempts, maxRecoveryAttempts, updateCircuitBreakerMetrics, scheduleRecovery]);

  // Calculate next interval with exponential backoff
  const calculateNextInterval = useCallback((baseInterval: number, retries: number): number => {
    if (retries === 0) return baseInterval;
    
    const backoffInterval = baseInterval * Math.pow(backoffMultiplier, retries);
    return Math.min(backoffInterval, safeMaxInterval);
  }, [backoffMultiplier, safeMaxInterval]);

  // Execute polling callback with error handling
  const executePoll = useCallback(async (): Promise<boolean> => {
    if (isCircuitOpen && !circuitBreakerState.isRecovering) {
      return false;
    }

    try {
      setLastAttemptTime(Date.now());
      const success = await callbackRef.current();
      
      if (success) {
        // Reset on success
        retryCountRef.current = 0;
        setRetryCount(0);
        setLastSuccessTime(Date.now());
        setCurrentInterval(isTabVisible ? safeActiveInterval : safeInactiveInterval);
        
        // Update circuit breaker metrics
        updateCircuitBreakerMetrics(true);
        
        // If this was a recovery attempt, complete the recovery
        if (circuitBreakerState.isRecovering) {
          setIsCircuitOpen(false);
          setCircuitBreakerState(prev => ({
            ...prev,
            isRecovering: false,
            recoveryAttempts: 0,
            openedAt: null,
          }));
          
          if (process.env.NODE_ENV === 'development') {
          debugLog('✅ Circuit breaker recovery completed during regular poll');
          }
        }
        
        return true;
      } else {
        // Handle failure
        // Only increment retry count if circuit is not open
        if (!isCircuitOpen) {
          const newRetryCount = retryCountRef.current + 1;
          retryCountRef.current = newRetryCount;
          setRetryCount(newRetryCount);
          
          // Apply exponential backoff for regular retries
          const baseInterval = isTabVisible ? safeActiveInterval : safeInactiveInterval;
          const nextInterval = calculateNextInterval(baseInterval, newRetryCount);
          setCurrentInterval(nextInterval);
        }
        
        // Update circuit breaker metrics after retry count is updated
        updateCircuitBreakerMetrics(false);
        
        return false;
      }
    } catch (error) {
      debugError('Smart polling callback error:', error);
      
      // Only increment retry count if circuit is not open
      if (!isCircuitOpen) {
        const newRetryCount = retryCountRef.current + 1;
        retryCountRef.current = newRetryCount;
        setRetryCount(newRetryCount);
        
        const baseInterval = isTabVisible ? safeActiveInterval : safeInactiveInterval;
        const nextInterval = calculateNextInterval(baseInterval, newRetryCount);
        setCurrentInterval(nextInterval);
      }
      
      // Update circuit breaker metrics for exceptions after retry count is updated
      updateCircuitBreakerMetrics(false);
      
      return false;
    }
  }, [isCircuitOpen, circuitBreakerState.isRecovering, isTabVisible, safeActiveInterval, safeInactiveInterval, updateCircuitBreakerMetrics, calculateNextInterval]);

  // Handle tab visibility changes with immediate refresh option
  useEffect(() => {
    const wasVisible = previousVisibilityRef.current;
    const isVisible = isTabVisible;
    
    // Update interval based on visibility
    if (isVisible) {
      setCurrentInterval(safeActiveInterval);
    } else {
      setCurrentInterval(safeInactiveInterval);
    }
    
    // Immediate refresh when tab becomes visible (if enabled and polling is active)
    if (immediateOnVisible && !wasVisible && isVisible && isPolling && !isCircuitOpen) {
      if (process.env.NODE_ENV === 'development') {
        debugLog('🔄 Tab became visible - immediate poll triggered');
      }
      
      // Execute immediate poll without affecting the regular polling schedule
      executePoll().catch(error => {
        debugError('Immediate poll on visibility change failed:', error);
      });
    }
    
    // Update previous visibility reference
    previousVisibilityRef.current = isVisible;
  }, [isTabVisible, safeActiveInterval, safeInactiveInterval, immediateOnVisible, isPolling, isCircuitOpen, executePoll]);

  // Start continuous polling
  const startContinuousPolling = useCallback(() => {
    clearAllTimers();
    
    if (isCircuitOpen) {
      return;
    }

    const poll = async () => {
      await executePoll();
      
      // Schedule next poll if still polling and circuit is closed
      if (isPollingRef.current && !isCircuitOpen) {
        pollingTimer.current = setTimeout(poll, currentInterval);
      }
    };

    // Start first poll
    pollingTimer.current = setTimeout(poll, currentInterval);
  }, [isCircuitOpen, currentInterval, executePoll, clearAllTimers]);

  // Start polling with optional initial delay
  const startPolling = useCallback((executeImmediately: boolean = false) => {
    if (isCircuitOpen) {
      return;
    }

    setIsPolling(true);
    isPollingRef.current = true; // Set ref immediately for synchronous access
    clearAllTimers();

    if (executeImmediately) {
      // Execute immediately then start continuous polling
      executePoll().then(() => {
        if (isPollingRef.current && !isCircuitOpen) {
          startContinuousPolling();
        }
      });
    } else {
      // Wait for initial delay then start continuous polling
      initialDelayTimer.current = setTimeout(() => {
        if (isPollingRef.current && !isCircuitOpen) {
          startContinuousPolling();
        }
      }, safeInitialDelay);
    }
  }, [isCircuitOpen, safeInitialDelay, executePoll, startContinuousPolling, clearAllTimers]);

  // Stop polling and reset state
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    isPollingRef.current = false; // Set ref immediately for synchronous access
    clearAllTimers();
  }, [clearAllTimers]);

  // Reset circuit breaker and retry count
  const resetCircuit = useCallback(() => {
    setIsCircuitOpen(false);
    retryCountRef.current = 0;
    setRetryCount(0);
    setCurrentInterval(isTabVisible ? safeActiveInterval : safeInactiveInterval);
    
    // Reset circuit breaker state
    setCircuitBreakerState({
      failureRate: 0,
      failuresInWindow: 0,
      attemptsInWindow: 0,
      openedAt: null,
      recoveryAttempts: 0,
      isRecovering: false,
    });
    
    // Clear attempts history
    attemptsHistory.current = [];
    
    // Clear recovery timer
    if (recoveryTimer.current) {
      clearTimeout(recoveryTimer.current);
      recoveryTimer.current = null;
    }
    
    if (process.env.NODE_ENV === 'development') {
      debugLog('🔄 Circuit breaker manually reset');
    }
  }, [isTabVisible, safeActiveInterval, safeInactiveInterval]);

  // Manual trigger for immediate poll
  const triggerPoll = useCallback(async (): Promise<boolean> => {
    return await executePoll();
  }, [executePoll]);

  // Handle automatic recovery attempts
  useEffect(() => {
    if (circuitBreakerState.isRecovering && !isPolling) {
      attemptRecovery();
    }
  }, [circuitBreakerState.isRecovering, isPolling, attemptRecovery]);

  // Update polling behavior when isPolling or circuit state changes
  useEffect(() => {
    if (isPolling && !isCircuitOpen) {
      startContinuousPolling();
    } else {
      clearAllTimers();
    }
  }, [isPolling, isCircuitOpen, startContinuousPolling, clearAllTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // Return state and control functions
  return {
    state: {
      isPolling,
      isActive: isTabVisible,
      retryCount,
      isCircuitOpen,
      currentInterval,
      lastSuccessTime,
      lastAttemptTime,
      circuitBreakerState,
    },
    startPolling,
    stopPolling,
    resetCircuit,
    triggerPoll,
    attemptRecovery,
  };
};

export default useSmartPolling;
